import { Component, OnInit, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_TOKEN, Environment } from '@core/models/environment.model';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { AuthLogoutEventType, AuthService } from '@core/services/auth/auth.service';
import { CookieConsentService } from '@core/services/cookie/cookie-consent.service';
import { MopinionService } from '@core/services/mopinion/mopinion.service';
import { UserStoreService } from '@core/services/user-store/user-store.service';
import { TranslateService } from '@ngx-translate/core';
import { NavbarMenuItem, NavbarService } from '@shared/services/navbar/navbar.service';
import { UpdateService } from '@shared/services/update/update.service';
import { ButtonType } from '@teamsp/components/button';
import { fromEvent } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit {
  isLoggedIn = false;
  navbarMenuItems: NavbarMenuItem[] = [];
  canAccessZendeskWidget = false;
  isTokenDispatched = false;
  hasNewFeatures = false;
  showCookieSettingsButton: boolean;
  ButtonType = ButtonType;

  private env = inject<Environment>(ENVIRONMENT_TOKEN);
  private analyticsService = inject(AnalyticsService);
  private authService = inject(AuthService);
  private cookieConsentService = inject(CookieConsentService);
  private mopinion = inject(MopinionService);
  private navbarService = inject(NavbarService);
  private translate = inject(TranslateService);
  private updateService = inject(UpdateService);
  private userService = inject(UserStoreService);

  authToken = computed(() => this.authService.token());
  environmentName = this.env.name;
  isNavBarReady = toSignal(
    fromEvent(document, 'navbarHeaderReady').pipe(map((event: CustomEvent<boolean>) => event.detail))
  );

  private _dispatchTokenEffect = effect(() => {
    const token = this.authService.token();
    const isNavBarReady = this.isNavBarReady();
    if (token && isNavBarReady) {
      // dispatch the new token to the navbar
      document.dispatchEvent(new CustomEvent('navbar_UpdateToken', { detail: this.authService.token() }));
      this.isTokenDispatched = true;
    }
  });

  get cultureCode() {
    return this.translate.currentLang;
  }

  ngOnInit() {
    // start listening for new versions of the application
    this.updateService.listen();
    this.translate.setDefaultLang('en-GB');
    // handle application events
    fromEvent(window, 'navbarAccountLogOut')
      .pipe(mergeMap(() => this.authService.logout(AuthLogoutEventType.USER)))
      .subscribe();

    fromEvent(window, 'navbarLanguageSelected').subscribe((event: CustomEvent) =>
      this.translate.use(event.detail.culture)
    );

    this.cookieConsentService.loadCookieScript().then(() => {
      this.onCookiesConsentUpdated();
      this.showCookieSettingsButton = true;
    });

    // Listen to consent changes and reinitialize adobe analytics
    this.cookieConsentService.onConsentChanged$.subscribe(() => this.onCookiesConsentUpdated());

    this.userService.isAuthenticated$.pipe(filter((isAuthenticated) => !!isAuthenticated)).subscribe(() => {
      this.isLoggedIn = true;
      this.translate.use(this.userService.profile.language);
      this.navbarMenuItems = this.navbarService.getMenuItems();
      this.mopinion.load(this.cultureCode);
      this.analyticsService.init(this.userService.profile);
      this.cookieConsentService.updateCookieBannerLanguage(this.cultureCode);
      this.hasNewFeatures = true;
    });
  }

  private onCookiesConsentUpdated() {
    if (this.userService.profile) {
      this.analyticsService.init(this.userService.profile);
    }
  }

  openOneTrustModal(): void {
    this.cookieConsentService.openOneTrustModal();
  }

  goToPrivacyPage() {
    const myScaniaPrivacyUrl = `${this.env.myScaniaURL}/privacy`;
    window.open(myScaniaPrivacyUrl, '_blank');
  }
}
